import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getContentFromCache, getTranslation } from '../api/contentApi';

interface GlossaryTooltipProps {
  /** The glossary entry id (matches ContentDocument.id) */
  entryId: string;
  /** Display text for the link */
  children: React.ReactNode;
  /** Current language code, e.g. "de" */
  lang: string;
}

/**
 * Renders an inline glossary term that shows a popover on hover/focus with
 * the term's explanation. Clicking navigates to the glossary anchor.
 * Reads from the already-warmed content cache — no extra network requests.
 */
const GlossaryTooltip: React.FC<GlossaryTooltipProps> = ({ entryId, children, lang }) => {
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVisible(true);
  };

  const hide = () => {
    hideTimer.current = setTimeout(() => setVisible(false), 150);
  };

  // Clean up timer on unmount
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  // Resolve glossary entry from cache (already warmed at app startup)
  const entries = getContentFromCache('glossary') ?? [];
  const entry = entries.find((e) => e.id === entryId);
  const tr = entry ? getTranslation(entry, lang) : null;

  return (
    <span
      ref={containerRef}
      className="relative inline"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <Link
        to={`/${lang}/glossary#glossary-${entryId}`}
        className="text-primary-600 underline decoration-dotted hover:decoration-solid focus:outline-none"
        aria-describedby={`glossary-tip-${entryId}`}
      >
        {children}
      </Link>

      {visible && tr && (
        <span
          id={`glossary-tip-${entryId}`}
          role="tooltip"
          onMouseEnter={show}
          onMouseLeave={hide}
          className="
            absolute z-50 left-0 top-full mt-2
            w-72 max-w-xs
            bg-white border border-primary-200 rounded-lg shadow-lg
            p-3 text-sm text-gray-700
            pointer-events-auto
          "
        >
          <p className="font-semibold text-primary-800 mb-1">{tr.term}</p>
          {/* Strip HTML tags for tooltip text to keep it plain */}
          <p className="line-clamp-4">{tr.explanation?.replace(/<[^>]+>/g, '') ?? ''}</p>
          <Link
            to={`/${lang}/glossary#glossary-${entryId}`}
            className="block mt-2 text-xs text-primary-500 hover:underline"
          >
            {lang === 'de' ? 'Im Glossar lesen →' : lang === 'fr' ? 'Lire dans le glossaire →' : 'Read in glossary →'}
          </Link>
        </span>
      )}
    </span>
  );
};

export default GlossaryTooltip;
